import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetSet, Styles } from '@mkp/shared/models';
import { ASSET_STYLES } from './asset.styles';
import { SvgCacheService } from './services/svg-cache.service';

@Component({
  selector: 'ui-asset',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './asset.component.scss',
})
export class AssetComponent {
  public readonly elementRef = inject(ElementRef<HTMLElement>);

  name = input.required<string>();
  set = input<AssetSet>('icons');
  styles = input<Styles>({});

  private readonly renderer = inject(Renderer2);
  private readonly svgCache = inject(SvgCacheService);
  private readonly destroy = inject(DestroyRef);

  _ = effect(() => {
    const assetName = this.name().toLowerCase();
    const assetSet = this.set();

    this.svgCache
      .getSvgWithMemoryCache(assetName, assetSet)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((svg) => {
        this.setSvg(svg);
        this.setStyles(assetName);
        // Cache successful SVG loads in memory to avoid future HTTP requests
        if (svg) {
          this.svgCache.cacheInMemory(assetName, svg);
        }
      });
  });

  private setSvg(svg: string | undefined): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'innerHTML', svg ?? '');
  }

  private setStyles(name: string): void {
    const svg = this.elementRef.nativeElement.querySelector('svg');
    const componentStyles = this.styles();
    const styles = {
      ...(ASSET_STYLES.find((asset) => asset.name === name)?.styles || {}),
      ...componentStyles,
    };
    if (svg) {
      this.renderer.setAttribute(svg, 'fill', 'currentColor');
      Object.entries(styles).forEach(([key, value]) => {
        this.renderer.setStyle(svg, key, value);
      });
    }
  }
}
